import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { clsx } from "clsx";
import { Check, Pencil, Plus, Undo2, X } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Spinner } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import * as api from "../api";
import { useInstitutions, useLedMutation, useParcelCard, useTitleOptions } from "../hooks";
import {
  ENTRY_KIND_LABEL,
  INSTITUTION_KIND_LABEL,
  TITLE_STATUS_LABEL,
  type EntryKind,
  type InstitutionKind,
  type LedCardOwner,
  type LedEntry,
  type LedParcelCard,
  type PersonInput,
  type RegisterRow,
  type TitleStatus,
} from "../types";
import { Empty, ErrorLine, Field, FileChips, FilePick, INPUT, INPUT_SM, ReasonBox, SideTag, TEXT_FIRM, TONE_OK, TONE_WAIT, fmtDate, fmtDateTime, today } from "./common";
import { staleTitleLabel } from "./ParcelsTable";

const TITLE_BADGE: Record<TitleStatus, "green" | "yellow" | "red" | "gray" | "blue"> = {
  secured: "green", in_progress: "yellow", problem: "red", refused: "red", not_required: "blue", not_started: "gray",
};
/** Lewa sztaba karty strony — kolor stanu tytułu, żeby kartę czytało się jednym rzutem oka. */
const TITLE_RAIL: Record<TitleStatus, string> = {
  secured: "#059669", in_progress: "#d97706", problem: "var(--color-primary)", refused: "var(--color-primary)",
  not_required: "#2563eb", not_started: "var(--color-border-strong)",
};
const DIRECT_STATUSES: TitleStatus[] = ["not_started", "in_progress", "problem", "refused", "not_required"];
const PERSON_FIELDS: { key: keyof PersonInput; label: string; wide?: boolean }[] = [
  { key: "full_name", label: "Imię i nazwisko", wide: true },
  { key: "pesel_nip", label: "PESEL / NIP" },
  { key: "phone", label: "Telefon" },
  { key: "email", label: "E-mail" },
  { key: "street", label: "Ulica" },
  { key: "building_no", label: "Nr" },
  { key: "postal_code", label: "Kod" },
  { key: "city", label: "Miejscowość" },
  { key: "ownership_share", label: "Udział" },
  { key: "comment", label: "Notatka (np. kontakt przez syna)", wide: true },
];

interface Props {
  leadershipId: string;
  parcelId: string | null;
  internal: boolean;
  contractorName: string;
  onClose: () => void;
}

/** Drobna akcja tekstowa — zamiast ściany obramowanych przycisków. */
function LinkAction({ children, onClick, active, disabled }: { children: React.ReactNode; onClick: () => void; active?: boolean; disabled?: boolean }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={clsx(
        "text-[12px] transition-colors disabled:opacity-50",
        active ? "font-semibold text-[var(--color-primary)]" : "text-[var(--color-text-muted)] hover:text-[var(--color-primary)]",
      )}
    >
      {children}
    </button>
  );
}

// ── wpisy działań ──────────────────────────────────────────────────────────────
/** Pisma z rejestru — na karcie działki tylko do odczytu; pełna obsługa jest
 *  w zakładce „Rejestr spraw", tutaj chodzi o to, żeby w ogóle było widać, że są. */
function LetterList({ letters }: { letters: RegisterRow[] }) {
  if (!letters.length) return null;
  return (
    <ul className="mt-1 space-y-1">
      {letters.map((l) => (
        <li
          key={l.id}
          className="border border-[var(--color-border-muted)] px-2 py-1 text-[12px]"
          style={{ borderLeft: `3px solid ${l.direction === "in" ? "#1d4ed8" : "var(--color-primary)"}` }}
        >
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="font-mono text-[11px] font-bold text-[var(--color-text-muted)]">{l.label}</span>
            <span className="font-semibold">{l.kind_display}</span>
            <span className="text-[var(--color-text-secondary)]">{l.counterparty}</span>
            <span className="ml-auto font-mono text-[11px] text-[var(--color-text-muted)]">{fmtDate(l.entry_date)}</span>
          </div>
          {(l.reference || l.response_due || l.answered_on) && (
            <div className="text-[11px] text-[var(--color-text-muted)]">
              {l.reference && <>znak {l.reference} · </>}
              {l.answered_on
                ? `odpowiedź ${fmtDate(l.answered_on)}`
                : l.response_due
                  ? `termin odpowiedzi ${fmtDate(l.response_due)}`
                  : ""}
            </div>
          )}
          <FileChips files={l.files} className="mt-1" />
        </li>
      ))}
    </ul>
  );
}

function EntryList({ entries, leadershipId, contractorName }: { entries: LedEntry[]; leadershipId: string; contractorName: string }) {
  const edit = useLedMutation(leadershipId, (a: { id: string; body: string }) => api.editEntry(leadershipId, a.id, { body: a.body }), ["card"]);
  const withdraw = useLedMutation(leadershipId, (a: { id: string; reason: string }) => api.withdrawEntry(leadershipId, a.id, a.reason), ["card"]);
  const [editing, setEditing] = useState<{ id: string; body: string } | null>(null);
  const [withdrawing, setWithdrawing] = useState<string | null>(null);
  if (!entries.length) return <Empty>Brak wpisów.</Empty>;
  return (
    <ol className="space-y-2">
      {entries.map((e) => (
        <li key={e.id} className={clsx("border-l-2 pl-2.5 text-[12px]", e.withdrawn ? "border-[var(--color-border)] opacity-60" : e.side === "gk" ? "border-[var(--color-primary)]" : "border-blue-700 dark:border-blue-400")}>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className="font-mono text-[11px] text-[var(--color-text-secondary)]">{fmtDate(e.occurred_on)}</span>
            <strong>{e.kind_display}</strong>
            <SideTag side={e.side} contractorName={contractorName} />
            <span className="text-[var(--color-text-muted)]">{e.author_label}</span>
            {e.register_label && <span className="font-mono text-[10px] text-[var(--color-text-secondary)]">{e.register_label}</span>}
            {e.edited && <span className="text-[10px] text-[var(--color-text-muted)]">poprawiony</span>}
            {e.can_edit && !e.withdrawn && editing?.id !== e.id && withdrawing !== e.id && (
              <span className="ml-auto inline-flex gap-2">
                <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]" title="Popraw treść" onClick={() => setEditing({ id: e.id, body: e.body })}><Pencil size={12} /></button>
                <button type="button" className="text-[var(--color-text-muted)] hover:text-[var(--color-primary)]" title="Wycofaj wpis" onClick={() => setWithdrawing(e.id)}><Undo2 size={12} /></button>
              </span>
            )}
          </div>
          {editing?.id === e.id ? (
            <div className="mt-1 flex gap-1">
              <textarea className={clsx(INPUT, "min-h-[3rem] flex-1")} value={editing.body} onChange={(ev) => setEditing({ id: e.id, body: ev.target.value })} />
              <Button size="sm" onClick={async () => { await edit.mutateAsync(editing); setEditing(null); }} disabled={edit.isPending} title="Zapisz"><Check size={12} /></Button>
              <Button size="sm" variant="ghost" onClick={() => setEditing(null)} title="Anuluj"><X size={12} /></Button>
            </div>
          ) : (
            <div className={clsx("whitespace-pre-wrap", e.withdrawn && "line-through")}>{e.body}</div>
          )}
          {withdrawing === e.id && (
            <ReasonBox label="Powód wycofania (zostaje w historii)" danger confirmLabel="Wycofaj wpis" busy={withdraw.isPending} onCancel={() => setWithdrawing(null)} onConfirm={async (reason) => { await withdraw.mutateAsync({ id: e.id, reason }); setWithdrawing(null); }} />
          )}
          {e.withdrawn && <div className="text-[11px] text-[var(--color-primary)]">wycofany: {e.withdraw_reason}</div>}
          <FileChips files={e.files} className="mt-1" />
          <ErrorLine error={edit.error || withdraw.error} />
        </li>
      ))}
    </ol>
  );
}

function AddEntryForm({ leadershipId, parcelId, owners, presetOwner, onDone }: { leadershipId: string; parcelId: string; owners: LedCardOwner[]; presetOwner?: string | null; onDone: () => void }) {
  const [kind, setKind] = useState<EntryKind>("phone");
  const [date, setDate] = useState(today());
  const [body, setBody] = useState("");
  const [owner, setOwner] = useState(presetOwner ?? "");
  const [files, setFiles] = useState<File[]>([]);
  const add = useLedMutation(leadershipId, () => api.addEntry(leadershipId, { kind, occurred_on: date, body, parcel: parcelId, owner: owner || null, files }), ["card"]);
  const ownerName = owners.find((o) => o.id === owner)?.full_name;
  return (
    <div className="mt-2 border border-[var(--color-border-strong)] bg-[var(--color-bg-surface)]">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--color-border)] bg-[var(--color-bg-elevated)] px-3 py-2">
        <span className="section-label">
          Notatka / informacja z terenu{ownerName ? ` · ${ownerName}` : " · cała działka"}
        </span>
        <span className="inline-flex">
          {(Object.keys(ENTRY_KIND_LABEL) as EntryKind[]).map((k, i) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={clsx(
                "border px-2 py-0.5 font-mono text-[10px] uppercase tracking-wider transition-colors",
                i > 0 && "-ml-px",
                kind === k
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]",
              )}
            >
              {ENTRY_KIND_LABEL[k]}
            </button>
          ))}
        </span>
      </div>
      <div className="space-y-3 p-3">
        <Field label="Treść">
          <textarea
            className={clsx(INPUT, "min-h-[5rem]")}
            autoFocus
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Co ustalono, co powiedział właściciel, co się wydarzyło w terenie"
          />
        </Field>
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Data zdarzenia" className="w-[10rem]">
            <DatePicker value={date} max={today()} clearable={false} className="!py-1 text-[13px]" onChange={(v) => setDate(v || today())} />
          </Field>
          <Field label="Dotyczy" className="min-w-[12rem] flex-1">
            <select className={INPUT_SM} value={owner} onChange={(e) => setOwner(e.target.value)}>
              <option value="">— cała działka —</option>
              {owners.map((o) => (
                <option key={o.id} value={o.id}>{o.full_name}</option>
              ))}
            </select>
          </Field>
          <FilePick
            variant="secondary"
            onFiles={(f) => setFiles((cur) => [...cur, ...f])}
            label={files.length ? `${files.length} plik(i): ${files.map((f) => f.name).join(", ")}` : "Dodaj skan / zdjęcie"}
          />
          {files.length > 0 && (
            <Button size="sm" variant="ghost" onClick={() => setFiles([])}>wyczyść pliki</Button>
          )}
        </div>
        <ErrorLine error={add.error} />
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
          <Button
            size="sm"
            disabled={!body.trim() || add.isPending}
            onClick={async () => {
              await add.mutateAsync();
              onDone();
            }}
          >
            Zapisz notatkę
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── tytuł prawny ───────────────────────────────────────────────────────────────
function TitleReportForm({ leadershipId, owner, onDone }: { leadershipId: string; owner: LedCardOwner; onDone: () => void }) {
  const { data: opts } = useTitleOptions(leadershipId);
  const [basis, setBasis] = useState("");
  const [consent, setConsent] = useState("");
  const [doc, setDoc] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const report = useLedMutation(leadershipId, () => api.reportTitle(leadershipId, owner.id, { basis_code: basis, z7_consent_form: consent, document_number: doc, note, files }), ["card"]);
  useEffect(() => { if (!basis && opts?.bases[0]) setBasis(opts.bases[0].code); }, [opts, basis]);
  return (
    <div className={clsx("mt-2 border p-3", TONE_OK)}>
      <div className="mb-2 text-[13px] font-bold">Zgłoś pozyskanie tytułu — potwierdza GK ELEKTIM</div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Podstawa">
          <select className={INPUT} value={basis} onChange={(e) => setBasis(e.target.value)}>
            {(opts?.bases ?? []).map((b) => <option key={b.code} value={b.code}>{b.label}</option>)}
          </select>
        </Field>
        <Field label="Forma zgody Z7 (opcjonalnie)">
          <select className={INPUT} value={consent} onChange={(e) => setConsent(e.target.value)}>
            <option value="">—</option>
            {(opts?.consent_forms ?? []).map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Numer dokumentu / aktu"><input className={INPUT} value={doc} onChange={(e) => setDoc(e.target.value)} placeholder="np. zgoda 12/2026, Rep. A 1234/2026" /></Field>
        <Field label="Uwaga"><input className={INPUT} value={note} onChange={(e) => setNote(e.target.value)} /></Field>
      </div>
      <FileDropZone files={files} onFilesChange={setFiles} multiple className="mt-3" label="Skan zgody / decyzji — można kilka plików" />
      <ErrorLine error={report.error} />
      <div className="mt-3 flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
        <Button size="sm" disabled={!basis || report.isPending} onClick={async () => { await report.mutateAsync(); onDone(); }}>Zgłoś do potwierdzenia</Button>
      </div>
    </div>
  );
}

function PendingReportBox({ leadershipId, owner, internal }: { leadershipId: string; owner: LedCardOwner; internal: boolean }) {
  const pending = owner.pending_report!;
  const decide = useLedMutation(leadershipId, (a: { decision: "confirm" | "reject"; note: string }) => api.decideTitleReport(leadershipId, pending.id, a.decision, a.note), ["card"]);
  const [rejecting, setRejecting] = useState(false);
  // Decyzja = bramka modułu: dyr. techniczny / CEO / flaga „Potwierdzanie tytułów od firm
  // (ZE)” od CEO (lustro has_led_title_confirmation_permission na backendzie). Pozostali
  // z modułem widzą zgłoszenie i załączniki, ale bez przycisków.
  const canDecide = internal && !!useAuthStore((s) => s.user)?.has_led_title_confirmation_permission;
  return (
    <div className={clsx("mt-2 border p-3 text-[12px]", TONE_WAIT)}>
      <div className="text-[13px] font-bold">Zgłoszone pozyskanie tytułu — {internal ? "czeka na NASZE potwierdzenie" : "czeka na potwierdzenie GK ELEKTIM"}</div>
      <div className="mt-1 text-[var(--color-text-secondary)]">
        {pending.basis_code}{pending.document_number && <> · {pending.document_number}</>} · {pending.reported_by_label} · {fmtDate(pending.reported_at)}{pending.note && <> · {pending.note}</>}
      </div>
      {/* Dowód (zgoda, decyzja, akt) otwiera się w przeglądarce PRZED decyzją — bez pobierania. */}
      <FileChips files={pending.files} className="mt-2" previewKind="led_title_file" />
      {canDecide && pending.files.length > 0 && !rejecting && (
        <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
          Kliknij nazwę pliku, żeby obejrzeć załącznik przed potwierdzeniem (PDF, skany i dokumenty otwierają się w przeglądarce).
        </p>
      )}
      {internal && pending.files.length === 0 && !rejecting && (
        <p className="mt-1.5 text-[11px] text-amber-800 dark:text-amber-300">Zgłoszenie bez załączników — firma nie dołączyła dokumentu.</p>
      )}
      {internal && !canDecide && (
        <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">
          Potwierdza dyrektor techniczny albo osoba z uprawnieniem „Potwierdzanie tytułów od firm (ZE)” nadanym przez CEO.
        </p>
      )}
      {canDecide && !rejecting && (
        <div className="mt-2.5 flex gap-2">
          <Button size="sm" disabled={decide.isPending} onClick={() => decide.mutate({ decision: "confirm", note: "" })}>Potwierdź tytuł</Button>
          <Button size="sm" variant="ghost" disabled={decide.isPending} onClick={() => setRejecting(true)}>Odrzuć…</Button>
        </div>
      )}
      {canDecide && rejecting && (
        <ReasonBox label="Powód odrzucenia (firma go zobaczy)" danger confirmLabel="Odrzuć zgłoszenie" busy={decide.isPending} onCancel={() => setRejecting(false)} onConfirm={async (note) => { await decide.mutateAsync({ decision: "reject", note }); setRejecting(false); }} />
      )}
      <ErrorLine error={decide.error} />
    </div>
  );
}

// ── strona (właściciel / instytucja) ───────────────────────────────────────────
type Panel = "none" | "report" | "edit" | "marital" | "entry" | "history";

function OwnerCard({ leadershipId, card, owner, internal, contractorName }: { leadershipId: string; card: LedParcelCard; owner: LedCardOwner; internal: boolean; contractorName: string }) {
  const [panel, setPanel] = useState<Panel>("none");
  const [draft, setDraft] = useState<Partial<PersonInput>>({});
  const [spouse, setSpouse] = useState("");
  const update = useLedMutation(leadershipId, (body: Partial<PersonInput>) => api.updateOwner(leadershipId, owner.id, body), ["card"]);
  const status = useLedMutation(
    leadershipId,
    (a: { status: string; note?: string }) => api.setTitleStatus(leadershipId, owner.id, a),
    ["card"],
  );
  const [statusNote, setStatusNote] = useState<{ status: string } | null>(null);
  const join = useLedMutation(leadershipId, (sp: string) => api.maritalJoin(leadershipId, owner.id, sp), ["card"]);
  const leave = useLedMutation(leadershipId, () => api.maritalLeave(leadershipId, owner.id), ["card"]);
  const extend = useLedMutation(leadershipId, () => api.extendOwnerTitle(leadershipId, owner.id), ["card"]);
  const others = card.owners.filter((o) => o.id !== owner.id && o.kind === "person" && !o.marital_group);
  const isPerson = owner.kind === "person";
  const pending = owner.pending_report;
  const secured = owner.title_status === "secured";
  const toggle = (p: Panel) => setPanel((cur) => (cur === p ? "none" : p));
  const contact = [owner.pesel_nip && `PESEL/NIP ${owner.pesel_nip}`, owner.phone && `tel. ${owner.phone}`, owner.email].filter(Boolean).join(" · ");

  return (
    <li className="card" style={{ borderLeft: `3px solid ${secured && owner.title_stale ? TITLE_RAIL.in_progress : TITLE_RAIL[owner.title_status]}` }}>
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-2 px-3 pt-3">
        <div className="min-w-0 flex-1 basis-[16rem]">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className="text-sm font-bold">{owner.full_name}</span>
            {!isPerson && <span className={clsx("font-mono text-[10px] uppercase tracking-wider", TEXT_FIRM)}>instytucja</span>}
            {owner.share && <span className="text-[12px] text-[var(--color-text-secondary)]">udział {owner.share}</span>}
            {owner.marital_group && <span className="text-[12px] text-[var(--color-text-secondary)]">· wspólność małżeńska</span>}
          </div>
          {owner.address && <div className="text-[12px] text-[var(--color-text-muted)]">{owner.address}</div>}
          <div className="font-mono text-[11px] text-[var(--color-text-muted)]">{contact || "brak kontaktu"}</div>
        </div>
        <div className="shrink-0 text-right">
          {secured && owner.title_stale ? (
            <Badge color="yellow">{staleTitleLabel(owner)}</Badge>
          ) : (
            <Badge color={TITLE_BADGE[owner.title_status]}>{owner.title_status_display}</Badge>
          )}
          {owner.secured_at && <div className="mt-1 text-[11px] text-[var(--color-text-muted)]">{owner.title_basis} · {fmtDate(owner.secured_at)}{owner.title_route_version && !owner.title_stale ? ` · trasa ${owner.title_route_version}` : ""}</div>}
        </div>
      </div>

      {/* Tytuł pozyskany dla starej wersji trasy: nie uzgadnia działki. GK rozciąga go na
          bieżącą wersję (gdy zgoda obejmuje nowy przebieg); firma zgłasza nowy tytuł. */}
      {secured && owner.title_stale && (
        <div className={clsx("mx-3 mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 border px-2.5 py-2 text-[12px]", TONE_WAIT)}>
          <span className="min-w-0 flex-1">
            {owner.title_route_version
              ? <>Tytuł pozyskano dla wersji trasy <strong className="font-mono">{owner.title_route_version}</strong></>
              : <>Tytuł pozyskano bez wersji trasy</>}
            {card.route_change?.current_version
              ? <>, aktualna to <strong className="font-mono">{card.route_change.current_version}</strong>.</>
              : <>, a projekt nie ma teraz bieżącej wersji trasy.</>}
            {" "}Dopóki nie zostanie rozciągnięty na aktualną wersję, działka nie liczy się jako uzgodniona.
            {!internal && " Jeśli zgoda nie obejmuje nowego przebiegu, zgłoś nowy tytuł."}
          </span>
          {internal && card.can_resolve_route_change && (
            <Button size="sm" variant="secondary" loading={extend.isPending} onClick={() => extend.mutate()}>
              Rozciągnij na aktualną wersję{card.route_change?.current_version ? ` (${card.route_change.current_version})` : ""}
            </Button>
          )}
          <ErrorLine error={extend.error} className="basis-full" />
        </div>
      )}

      <div className="px-3">{pending && <PendingReportBox leadershipId={leadershipId} owner={owner} internal={internal} />}</div>

      <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-dashed border-[var(--color-border-muted)] px-3 py-2">
        {!pending && (!secured || internal) && (
          <label className="inline-flex items-center gap-1.5 text-[12px]">
            <span className="section-label">stan</span>
            <select
              className={clsx(INPUT_SM, "w-auto")}
              value={owner.title_status}
              disabled={status.isPending}
              onChange={(e) => {
                const next = e.target.value;
                // „Problem" i „Odmowa" bez zdania wyjaśnienia są bezużyteczne dla drugiej
                // strony — pytamy o powód od razu, resztę stanów zapisujemy wprost.
                if (next === "problem" || next === "refused") setStatusNote({ status: next });
                else status.mutate({ status: next });
              }}
            >
              {(secured ? (["secured", ...DIRECT_STATUSES] as TitleStatus[]) : DIRECT_STATUSES).map((s) => (
                <option key={s} value={s} disabled={s === "secured"}>{TITLE_STATUS_LABEL[s]}</option>
              ))}
            </select>
          </label>
        )}
        {!pending && !secured && <Button size="sm" variant={panel === "report" ? "primary" : "secondary"} onClick={() => toggle("report")}>Zgłoś pozyskanie tytułu</Button>}
        <Button
          size="sm"
          variant={panel === "entry" ? "primary" : "secondary"}
          onClick={() => toggle("entry")}
        >
          <Plus size={13} /> Notatka / z terenu
        </Button>
        <Button
          size="sm"
          variant={panel === "edit" ? "primary" : "ghost"}
          onClick={() => { setDraft({ full_name: owner.full_name, pesel_nip: owner.pesel_nip, phone: owner.phone, email: owner.email }); toggle("edit"); }}
        >
          <Pencil size={12} /> Dane i kontakt
        </Button>
        {isPerson && !owner.marital_group && others.length > 0 && <LinkAction active={panel === "marital"} onClick={() => toggle("marital")}>małżeństwo</LinkAction>}
        {owner.marital_group && <LinkAction disabled={leave.isPending} onClick={() => leave.mutate()}>rozłącz małżeństwo</LinkAction>}
        {owner.status_history.length > 0 && (
          <LinkAction active={panel === "history"} onClick={() => toggle("history")}>
            historia stanów ({owner.status_history.length})
          </LinkAction>
        )}
      </div>

      <div className="px-3 pb-3">
        <ErrorLine error={status.error || update.error || join.error || leave.error} />
        {panel === "report" && <TitleReportForm leadershipId={leadershipId} owner={owner} onDone={() => setPanel("none")} />}
        {panel === "marital" && (
          <div className="mt-2 flex flex-wrap items-end gap-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
            <Field label="Współmałżonek (ta sama działka)" className="min-w-[14rem] flex-1">
              <select className={INPUT} value={spouse} onChange={(e) => setSpouse(e.target.value)}>
                <option value="">— wybierz —</option>
                {others.map((o) => <option key={o.id} value={o.id}>{o.full_name}</option>)}
              </select>
            </Field>
            <Button size="sm" disabled={!spouse || join.isPending} onClick={async () => { await join.mutateAsync(spouse); setSpouse(""); setPanel("none"); }}>Połącz</Button>
            <Button size="sm" variant="ghost" onClick={() => setPanel("none")}>Anuluj</Button>
          </div>
        )}
        {panel === "edit" && (
          <div className="mt-2 grid grid-cols-2 gap-3 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 sm:grid-cols-4">
            {PERSON_FIELDS.map((f) => (
              <Field key={f.key} label={f.label} className={clsx(f.wide && "col-span-2")}>
                <input className={INPUT_SM} value={(draft[f.key] as string) ?? ""} onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))} />
              </Field>
            ))}
            <div className="col-span-2 flex justify-end gap-2 sm:col-span-4">
              <Button size="sm" variant="ghost" onClick={() => setPanel("none")}>Anuluj</Button>
              <Button size="sm" disabled={update.isPending} onClick={async () => { await update.mutateAsync(draft); setPanel("none"); }}>Zapisz</Button>
            </div>
          </div>
        )}
        {statusNote && (
          <ReasonBox
            label={`Powód: ${TITLE_STATUS_LABEL[statusNote.status as TitleStatus]} (zobaczy to druga strona)`}
            confirmLabel="Zapisz stan"
            busy={status.isPending}
            onCancel={() => setStatusNote(null)}
            onConfirm={async (note) => {
              await status.mutateAsync({ status: statusNote.status, note });
              setStatusNote(null);
            }}
          />
        )}
        {panel === "history" && (
          <ol className="mt-2 space-y-1 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
            {owner.status_history.map((h, i) => (
              <li key={i} className="flex flex-wrap items-baseline gap-x-2 text-[12px]">
                <span className="font-mono text-[11px] text-[var(--color-text-muted)]">{fmtDateTime(h.at)}</span>
                <span>
                  {h.from_display ? `${h.from_display} → ` : ""}
                  <strong>{h.to_display}</strong>
                </span>
                <span className="text-[var(--color-text-muted)]">{h.by_label}</span>
                {h.note && <span className="basis-full text-[var(--color-text-secondary)]">„{h.note}”</span>}
              </li>
            ))}
          </ol>
        )}
        {panel === "entry" && <AddEntryForm leadershipId={leadershipId} parcelId={card.id} owners={card.owners} presetOwner={owner.id} onDone={() => setPanel("none")} />}
        {owner.letters.length > 0 && (
          <div className="mt-3">
            <div className="section-label mb-1.5">Korespondencja z tą stroną · {owner.letters.length}</div>
            <LetterList letters={owner.letters} />
          </div>
        )}
        {owner.entries.length > 0 && (
          <div className="mt-3">
            <div className="section-label mb-1.5">Historia działań z tą stroną</div>
            <EntryList entries={owner.entries} leadershipId={leadershipId} contractorName={contractorName} />
          </div>
        )}
      </div>
    </li>
  );
}

// ── dodawanie strony ───────────────────────────────────────────────────────────
function AddPartyPanel({ leadershipId, card, onDone }: { leadershipId: string; card: LedParcelCard; onDone: () => void }) {
  const [mode, setMode] = useState<"person" | "institution">("person");
  const [person, setPerson] = useState<PersonInput>({ full_name: "" });
  const [inst, setInst] = useState("");
  const [role, setRole] = useState("");
  const [newInst, setNewInst] = useState<{ name: string; kind: InstitutionKind; city: string; phone: string; email: string } | null>(null);
  const { data: institutions } = useInstitutions(leadershipId);
  const addPerson = useLedMutation(leadershipId, () => api.addPerson(leadershipId, card.id, person), ["card"]);
  const attach = useLedMutation(leadershipId, (id: string) => api.attachInstitution(leadershipId, card.id, { institution: id, role }), ["card"]);
  const create = useLedMutation(leadershipId, () => api.createInstitution(leadershipId, newInst!), ["card"]);
  return (
    <div className="mb-2 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3">
      <div className="mb-3 flex gap-4 text-[13px]">
        <label className="inline-flex items-center gap-1.5"><input type="radio" checked={mode === "person"} onChange={() => setMode("person")} /> osoba</label>
        <label className="inline-flex items-center gap-1.5"><input type="radio" checked={mode === "institution"} onChange={() => setMode("institution")} /> instytucja / urząd</label>
      </div>
      {mode === "person" ? (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {PERSON_FIELDS.map((f) => (
              <Field key={f.key} label={f.label} className={clsx(f.wide && "col-span-2")}>
                <input className={INPUT_SM} value={(person[f.key] as string) ?? ""} onChange={(e) => setPerson((p) => ({ ...p, [f.key]: e.target.value }))} />
              </Field>
            ))}
          </div>
          <ErrorLine error={addPerson.error} />
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
            <Button size="sm" disabled={!person.full_name.trim() || addPerson.isPending} onClick={async () => { await addPerson.mutateAsync(); onDone(); }}>Dodaj osobę</Button>
          </div>
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Instytucja z katalogu tego projektu">
              <select className={INPUT} value={inst} onChange={(e) => setInst(e.target.value)}>
                <option value="">— wybierz —</option>
                {(institutions ?? []).filter((i) => i.is_active).map((i) => <option key={i.id} value={i.id}>{i.name} ({i.kind_display})</option>)}
              </select>
            </Field>
            <Field label="Rola na tej działce (opcjonalnie)"><input className={INPUT} value={role} onChange={(e) => setRole(e.target.value)} placeholder="np. zarządca drogi, gestor sieci" /></Field>
          </div>
          {!newInst ? (
            <button type="button" className="mt-2 text-[12px] text-[var(--color-primary)] hover:underline" onClick={() => setNewInst({ name: "", kind: "gmina", city: "", phone: "", email: "" })}>+ nowa instytucja w katalogu projektu</button>
          ) : (
            <div className="mt-3 grid grid-cols-2 gap-3 border border-dashed border-[var(--color-border)] p-3 sm:grid-cols-3">
              <Field label="Nazwa" className="col-span-2"><input className={INPUT_SM} value={newInst.name} onChange={(e) => setNewInst({ ...newInst, name: e.target.value })} /></Field>
              <Field label="Rodzaj">
                <select className={INPUT_SM} value={newInst.kind} onChange={(e) => setNewInst({ ...newInst, kind: e.target.value as InstitutionKind })}>
                  {(Object.keys(INSTITUTION_KIND_LABEL) as InstitutionKind[]).map((k) => <option key={k} value={k}>{INSTITUTION_KIND_LABEL[k]}</option>)}
                </select>
              </Field>
              <Field label="Miejscowość"><input className={INPUT_SM} value={newInst.city} onChange={(e) => setNewInst({ ...newInst, city: e.target.value })} /></Field>
              <Field label="Telefon"><input className={INPUT_SM} value={newInst.phone} onChange={(e) => setNewInst({ ...newInst, phone: e.target.value })} /></Field>
              <Field label="E-mail"><input className={INPUT_SM} value={newInst.email} onChange={(e) => setNewInst({ ...newInst, email: e.target.value })} /></Field>
              <div className="col-span-2 flex justify-end sm:col-span-3">
                <Button size="sm" variant="secondary" disabled={!newInst.name.trim() || create.isPending} onClick={async () => { const list = await create.mutateAsync(); const created = list.find((i) => i.name === newInst.name.trim()); if (created) setInst(created.id); setNewInst(null); }}>Zapisz w katalogu</Button>
              </div>
              <ErrorLine error={create.error} />
            </div>
          )}
          <ErrorLine error={attach.error} />
          <div className="mt-3 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onDone}>Anuluj</Button>
            <Button size="sm" disabled={!inst || attach.isPending} onClick={async () => { await attach.mutateAsync(inst); onDone(); }}>Dodaj jako stronę</Button>
          </div>
        </>
      )}
    </div>
  );
}

// ── szuflada ───────────────────────────────────────────────────────────────────
/** Karta działki — prawa szuflada: strony, tytuły prawne, KW, droga wewnętrzna, wpisy działań. */
export function ParcelDrawer({ leadershipId, parcelId, internal, contractorName, onClose }: Props) {
  const { data: card, isLoading, error } = useParcelCard(leadershipId, parcelId);
  const [kw, setKw] = useState<string | null>(null);
  const [addingParty, setAddingParty] = useState(false);
  const [addingEntry, setAddingEntry] = useState(false);
  const saveKw = useLedMutation(leadershipId, (v: string) => api.setParcelKw(leadershipId, parcelId!, v), ["card"]);
  const road = useLedMutation(leadershipId, (v: boolean) => api.setParcelRoad(leadershipId, parcelId!, { is_internal_road: v, note: card?.road_note ?? "" }), ["card"]);
  const clearReview = useLedMutation(leadershipId, () => api.clearRouteReview(leadershipId, parcelId!), ["card"]);

  useEffect(() => {
    if (!parcelId) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, parcelId]);

  if (!parcelId) return null;
  // Tytuł pozyskany dla starej wersji trasy nie uzgadnia działki — tak liczy DLC 01.
  const secured = card
    ? card.owners.filter((o) => (o.title_status === "secured" && !o.title_stale) || o.title_status === "not_required").length
    : 0;
  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex justify-end bg-black/50 backdrop-blur-[1px]"
      onClick={onClose}
    >
      <aside
        role="dialog"
        aria-modal="true"
        className="flex h-full w-full max-w-3xl min-w-0 flex-col overflow-x-hidden border-l-2 border-[var(--color-text-primary)] bg-[var(--color-bg-base)] shadow-[-12px_0_40px_-12px_rgba(0,0,0,0.45)]"
        onClick={(e) => e.stopPropagation()}
      >
        {isLoading || !card ? (
          <div className="flex h-40 items-center justify-center">{error ? <ErrorLine error={error} /> : <Spinner />}</div>
        ) : (
          <>
            <header className="flex min-w-0 items-start justify-between gap-3 border-b border-[var(--color-border)] bg-[var(--color-bg-surface)] px-5 py-3">
              <div className="min-w-0">
                <div className="section-label">Karta działki</div>
                <h2 className="font-mono text-xl font-bold leading-tight">{card.teryt}</h2>
                <div className="mt-0.5 text-[12px] text-[var(--color-text-secondary)]">
                  {card.obreb}{card.commune && <>, {card.commune}</>}
                  {card.intersection_length_m != null && <> · {Math.round(card.intersection_length_m)} m w trasie</>}
                  {card.area_m2 != null && <> · {Math.round(card.area_m2)} m²</>}
                </div>
                {card.address && <div className="text-[12px] text-[var(--color-text-muted)]">{card.address}</div>}
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Badge color={card.in_current_route ? "blue" : "gray"}>{card.in_current_route ? "w trasie" : "poza trasą"}</Badge>
                  {card.is_public && <Badge color="blue">działka publiczna</Badge>}
                  {card.is_internal_road && <Badge color="gray">droga wewnętrzna</Badge>}
                  {card.owners.length > 0 && <Badge color={secured === card.owners.length ? "green" : "gray"}>tytuły {secured}/{card.owners.length}</Badge>}
                  {card.status === "ok" && (
                    <Badge color="green">{card.status_locked ? "uzgodniona (status ręczny)" : "uzgodniona"}</Badge>
                  )}
                  {card.status !== "ok" && card.route_change && (
                    <Badge color="yellow">{card.status_display.toLowerCase()} · zmiana trasy</Badge>
                  )}
                </div>
              </div>
              <button type="button" className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]" onClick={onClose} aria-label="Zamknij"><X size={18} /></button>
            </header>

            <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-5 pb-10">
              {/* Dlaczego taki status: zmiana trasy (flaga DLC 01) i tytuły na starej wersji —
                  obie strony widzą przyczynę; GK domyka („zweryfikowano", rozciągnięcie przy stronie). */}
              {card.route_change && card.status_reason && (
                <div className={clsx("mt-4 border px-3 py-2.5 text-[13px]", TONE_WAIT)} data-testid="route-change-banner">
                  <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-widest text-amber-800 dark:text-amber-300">
                    Dlaczego taki status
                  </div>
                  <p>{card.status_reason}</p>
                  {card.route_change.review_needed && internal && card.can_resolve_route_change && (
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <Button size="sm" variant="secondary" loading={clearReview.isPending} onClick={() => clearReview.mutate()}>
                        Zmiana trasy zweryfikowana
                      </Button>
                      <span className="text-[11px] text-[var(--color-text-muted)]">
                        zdejmuje flagę „do weryfikacji"; tytuły na starej wersji rozciąga się przy stronie
                      </span>
                    </div>
                  )}
                  <ErrorLine error={clearReview.error} />
                </div>
              )}
              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <div className="card p-3">
                  <div className="section-label mb-1.5">Księga wieczysta</div>
                  {kw === null ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[13px]">{card.kw_number || "—"}</span>
                      <Button size="sm" variant="ghost" onClick={() => setKw(card.kw_number)}><Pencil size={12} /> zmień</Button>
                    </div>
                  ) : (
                    <div className="flex gap-1">
                      <input className={clsx(INPUT_SM, "flex-1 font-mono")} autoFocus value={kw} onChange={(e) => setKw(e.target.value)} placeholder="KR1W/00012345/6" onKeyDown={async (e) => { if (e.key === "Enter") { await saveKw.mutateAsync(kw); setKw(null); } }} />
                      <Button size="sm" disabled={saveKw.isPending} onClick={async () => { await saveKw.mutateAsync(kw); setKw(null); }} title="Zapisz"><Check size={12} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setKw(null)} title="Anuluj"><X size={12} /></Button>
                    </div>
                  )}
                  <ErrorLine error={saveKw.error} />
                </div>
                <div className="card p-3">
                  <div className="section-label mb-1.5">Droga wewnętrzna</div>
                  <label className="inline-flex items-center gap-2 text-[13px]">
                    <input type="checkbox" checked={card.is_internal_road} disabled={road.isPending} onChange={(e) => road.mutate(e.target.checked)} />
                    działka jest drogą wewnętrzną (dojazdową)
                  </label>
                  {card.road_note && <div className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">{card.road_note}</div>}
                  <ErrorLine error={road.error} />
                </div>
              </div>

              <div className="mb-2 mt-5 flex items-center justify-between gap-2 border-b border-[var(--color-border)] pb-1.5">
                <h3 className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">Strony · {card.owners.length}</h3>
                <Button size="sm" variant="secondary" onClick={() => setAddingParty((v) => !v)}>{addingParty ? "Anuluj" : "+ strona"}</Button>
              </div>
              {addingParty && <AddPartyPanel leadershipId={leadershipId} card={card} onDone={() => setAddingParty(false)} />}
              {card.owners.length === 0 ? (
                <Empty>Brak stron — dodaj właściciela albo instytucję.</Empty>
              ) : (
                <ul className="space-y-2">
                  {card.owners.map((o) => <OwnerCard key={o.id} leadershipId={leadershipId} card={card} owner={o} internal={internal} contractorName={contractorName} />)}
                </ul>
              )}

              {card.letters.length > 0 && (
                <>
                  <div className="mb-2 mt-5 border-b border-[var(--color-border)] pb-1.5">
                    <h3 className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">
                      Korespondencja dotycząca działki · {card.letters.length}
                    </h3>
                  </div>
                  <LetterList letters={card.letters} />
                </>
              )}

              <div className="mb-2 mt-5 flex items-center justify-between gap-2 border-b border-[var(--color-border)] pb-1.5">
                <h3 className="font-mono text-[0.7rem] font-bold uppercase tracking-widest text-[var(--color-text-secondary)]">Wpisy dla całej działki · {card.entries.length}</h3>
                <Button size="sm" variant="secondary" onClick={() => setAddingEntry((v) => !v)}>{addingEntry ? "Anuluj" : "+ wpis"}</Button>
              </div>
              {addingEntry && <AddEntryForm leadershipId={leadershipId} parcelId={card.id} owners={card.owners} onDone={() => setAddingEntry(false)} />}
              <div className="mt-2"><EntryList entries={card.entries} leadershipId={leadershipId} contractorName={contractorName} /></div>
            </div>
          </>
        )}
      </aside>
    </div>,
    document.body,
  );
}
